import { type ReactNode, Component, type MutableRefObject, type LegacyRef } from 'react';

import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { SharedRespRelatedItem } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';

// helpers
import classnames from 'classnames';
import {
    trackSimilarItemClick,
    trackSimilarItemImpressions,
} from '../tracking/similarItemsTracking';
import { trackItemsDisplayed, trackItemClick } from '../../pdp/tracking/similarItemsTracking';

import { type PdpRespSimilarItemsGrid_similarItems$data } from './__generated__/PdpRespSimilarItemsGrid_similarItems.graphql';
import { type PdpRespSimilarItemsGrid_item$data } from './__generated__/PdpRespSimilarItemsGrid_item.graphql';
import { type PdpRespSimilarItemsGrid_user$data } from './__generated__/PdpRespSimilarItemsGrid_user.graphql';
import { type PdpRespSimilarItemsGrid_viewer$data } from './__generated__/PdpRespSimilarItemsGrid_viewer.graphql';
import { type itemTileType } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem';
import { type FavoritesLocation } from '../../utils/tracking/shared/favoritesTracking';

import {
    VisibilityTracker,
    type VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';

type GridItems = PdpRespSimilarItemsGrid_similarItems$data;
export type Props = {
    header?: ReactNode;
    subHeader?: ReactNode;
    relayVariables: {
        isClient: boolean;
    };
    isDark?: boolean;
    hideBar?: boolean;
    showLightBox?: boolean;
    similarItems: GridItems;
    currency?: string;
    gridItemWrapperClass?: string;
    gridWrapperClass?: string;
    gridHeaderClass?: string;
    user?: PdpRespSimilarItemsGrid_user$data | null | undefined;
    viewer?: PdpRespSimilarItemsGrid_viewer$data | null | undefined;
    gridLocation: string;
    favoritesLocation: FavoritesLocation;
    fetchFolder?: boolean;
    dataTn?: string;
    scrollToRef?: MutableRefObject<HTMLDivElement | undefined>;
    gridItemWrapperRef?: MutableRefObject<HTMLDivElement | undefined>;
    item: PdpRespSimilarItemsGrid_item$data;
    onContentVisible?: () => void;
    onComponentDidMount?: () => void;
    showNoReserveBadge?: boolean;
    tileType?: itemTileType;
    hideTileImagePadding?: boolean;
    showMeasurements?: boolean;
    isUnavailablePdp?: boolean;
    onRowImpression?: (args: { index: number }) => void;
    onLastItemVisibilityChange?: (args: VisibilityChangeCallback) => void;
};

type State = { contentIsVisible: boolean };
export class PdpRespSimilarItemsGridComponent extends Component<Props, State> {
    static defaultProps = {
        isDark: true,
        showLightBox: true,
        dataTn: 'pdp-similar-recommendation-grid',
        showNoReserveBadge: true,
    };

    constructor(props: Props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);
        this.hasFiredTracking = false;

        this.state = {
            contentIsVisible: false,
        };
    }

    componentDidMount(): void {
        if (this.props.onComponentDidMount) {
            this.props.onComponentDidMount();
        }
    }

    componentDidUpdate(): void {
        if (
            this.props?.similarItems?.[0]?.ecommerceTrackingParams &&
            this.state.contentIsVisible &&
            !this.hasFiredTracking &&
            !this.props.onRowImpression
        ) {
            this.hasFiredTracking = true;
            if (this.props.onContentVisible) {
                this.props.onContentVisible();
            } else {
                trackItemsDisplayed({
                    moduleType: 'PDP_SIMILAR_ITEMS',
                    actionValue: 'bottom fold',
                });
            }
            trackSimilarItemImpressions(this.props?.similarItems || [], this.props.gridLocation);
        }
    }

    hasFiredTracking: boolean;

    onContentVisible(): void {
        this.setState({ contentIsVisible: true });
    }

    onClickItem(index: number): void {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS', actionValue: 'bottom fold' });
        trackSimilarItemClick(index, this.props?.similarItems[index], this.props.gridLocation);
    }

    render(): ReactNode {
        const gridItems = this.props?.similarItems || [];

        if (!gridItems.length) {
            return null;
        }

        const { contentIsVisible } = this.state;
        const {
            isDark,
            currency = '',
            user = null,
            viewer = null,
            showLightBox,
            gridWrapperClass,
            gridItemWrapperClass,
            gridHeaderClass,
            favoritesLocation,
            header,
            subHeader,
            dataTn,
            scrollToRef,
            item,
            gridItemWrapperRef,
            hideBar,
            showNoReserveBadge,
            tileType,
            hideTileImagePadding,
            showMeasurements,
            isUnavailablePdp,
            onRowImpression,
            onLastItemVisibilityChange,
        } = this.props;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: favoritesLocation,
            disable: !isClient || !contentIsVisible,
            userId: user?.serviceId ?? null,
            items: gridItems,
            fetchFolder: this.props.fetchFolder,
        };

        return (
            <PdpRespSimilarItemsContainer
                isUnavailablePdp={isUnavailablePdp}
                isDark={isDark}
                hideBar={isDark || hideBar}
                header={header}
                subHeader={subHeader}
                scrollToRef={scrollToRef}
                gridWrapperClass={gridWrapperClass}
                gridHeaderClass={gridHeaderClass}
                dataTn={dataTn}
                item={item}
            >
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) => {
                        if (isVisible) {
                            this.onContentVisible();
                        }
                    }}
                />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in SharedRespRelatedItem */}
                    {({ props: favorites }: $TSFixMe) => (
                        <QuickViewProvider>
                            <div className="rowFlex">
                                {gridItems.map((gridItem, index) => (
                                    <div
                                        key={gridItem.serviceId}
                                        className={classnames(
                                            'colLg3 colMd6 colXs6',
                                            gridItemWrapperClass
                                        )}
                                        ref={gridItemWrapperRef as LegacyRef<HTMLDivElement>}
                                    >
                                        {/* Grid of items */}
                                        <SharedRespRelatedItem
                                            item={gridItem}
                                            index={index}
                                            onClick={() => this.onClickItem(index)}
                                            currency={currency}
                                            viewer={viewer}
                                            showNoReserveBadge={showNoReserveBadge}
                                            favorites={favorites}
                                            user={user}
                                            showLightBox={showLightBox}
                                            badgeBgColor={isDark ? 'white' : undefined}
                                            tileType={tileType}
                                            hideTileImagePadding={hideTileImagePadding}
                                            showMeasurements={showMeasurements}
                                        />
                                        {onLastItemVisibilityChange &&
                                            index === gridItems.length - 1 && (
                                                <VisibilityTracker
                                                    watchAfterFirstVisible
                                                    observerOptions={{
                                                        rootMargin: '40%',
                                                        threshold: 0.4,
                                                    }}
                                                    onVisibilityChange={onLastItemVisibilityChange}
                                                />
                                            )}
                                        {onRowImpression && index % 5 === 0 && (
                                            <VisibilityTracker
                                                observerOptions={{
                                                    rootMargin: '10%',
                                                }}
                                                onVisibilityChange={({ isVisible }) => {
                                                    if (isVisible) {
                                                        onRowImpression?.({ index });
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </PdpRespSimilarItemsContainer>
        );
    }
}

export const PdpRespSimilarItemsGrid = createFragmentContainer(PdpRespSimilarItemsGridComponent, {
    viewer: graphql`
        fragment PdpRespSimilarItemsGrid_viewer on Viewer {
            ...SharedRespRelatedItem_viewer
        }
    `,
    similarItems: graphql`
        fragment PdpRespSimilarItemsGrid_similarItems on Item @relay(plural: true) {
            serviceId
            title
            browseUrl
            categoryCode
            ecommerceTrackingParams
            ...FavoritesProvider_item
            ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
        }
    `,
    user: graphql`
        fragment PdpRespSimilarItemsGrid_user on User {
            serviceId
            ...SharedRespRelatedItem_user
        }
    `,
    item: graphql`
        fragment PdpRespSimilarItemsGrid_item on Item {
            ...PdpRespSimilarItemsContainer_item
        }
    `,
});
